import React from 'react';

import FocusLockOld from 'react-focus-lock';
import FocusLockNext from 'react-focus-lock-next';
import ScrollLock from 'react-scrolllock';

// This is an internal usage, so it should be safe to use banned import
// eslint-disable-next-line @atlaskit/design-system/no-banned-imports
import useAutoFocus from '@atlaskit/ds-lib/use-auto-focus';
import { SlideIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';

import { Container } from './styled';
import type { WrapperProps } from './types';

/*
  tabIndex is added to the container so that the text selection doesn't break
  If there is no such element with tabIndex=-1 between Lock and body, the text selection breaks since focusing body is implicitly allowed.
  https://github.com/theKashey/react-focus-lock/issues/124
 */
export const Wrapper = ({
	children,
	onAnimationFinish,
	autoFocus = false,
	whiteList,
	returnFocusRef,
	returnFocus,
	onDeactivation,
	label,
	titleId,
}: WrapperProps) => {
	const FocusLock = fg('platform_dst_modal-dialog-bump-focus-lock') ? FocusLockNext : FocusLockOld;

	// When a user supplies a ref to focus we skip auto focus via react-focus-lock
	const autoFocusLock = typeof autoFocus === 'boolean' ? autoFocus : false;

	const accessibilityAttributes = {
		role: 'dialog',
		'aria-modal': true,
		'aria-label': label,
		'aria-labelledby': titleId,
	};

	useAutoFocus(
		typeof autoFocus === 'object' ? autoFocus : undefined,
		// When a user supplies  a ref to focus we enable this hook
		typeof autoFocus === 'object',
	);

	return (
		<SlideIn
			enterFrom="left"
			exitTo="left"
			fade="in"
			duration="medium"
			onFinish={onAnimationFinish}
		>
			{(slideInProps) => (
				<FocusLock
					returnFocus={returnFocus}
					autoFocus={autoFocusLock}
					onDeactivation={(args) => {
						if (!!returnFocusRef) {
							window.setTimeout(() => {
								returnFocusRef.current?.focus();
							}, 0);
						}

						onDeactivation?.(args);
					}}
					whiteList={(element) => {
						if (element.role === 'alert') {
							return false; // False means it will not be controlled by the focus
						}

						return !!whiteList ? whiteList(element) : true;
					}}
				>
					<ScrollLock>
						<Container {...slideInProps} {...accessibilityAttributes} tabIndex={-1}>
							{children}
						</Container>
					</ScrollLock>
				</FocusLock>
			)}
		</SlideIn>
	);
};
